import React, { HTMLProps, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import Toggle from 'react-toggle';
import { PRIVACY_STATEMENT_PENSION } from '../../../config/constants';
import { RobotEnvironmentContext } from '../../../contexts/general/RobotEnvironmentContext';
import { Button } from '../../general/Button';
import { IconElements } from '../../../config/icons';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { Link } from '../../general/Link';

export const BNDListArrows: React.FC<HTMLProps<HTMLDivElement>> = ({ children }) => {
    return (
        <>
            {React.Children.map(children, (child) => {
                if (!child) return undefined;

                return (
                    <div className="flex justify-start items-center mt-3">
                        <div style={{ display: 'block' }}>
                            <IconElements.ArrowRight className="mr-3 h-4 text-bnd-green" />
                        </div>
                        <div className="text-black inline">{child as React.ReactNode}</div>
                    </div>
                );
            })}
        </>
    );
};

export const LegalSection: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const history = useHistory();
    const [toggleValue, setToggleValue] = useState(false);
    const { environment } = useContext(RobotEnvironmentContext);
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    const isReturning = !!personalInfo?.self?.age;

    const getLegalStatements = () =>
        environment === 'MyBND' ? getLegalStatementsMyBND() : getLegalStatementsMarketing();

    const getLegalStatementsMyBND = () => (
        <BNDListArrows>
            {t('pension-legal-page-list-item-1')}
            {t('pension-legal-page-list-item-2-mybnd')}
            {t('pension-legal-page-list-item-3-mybnd')}
            {t('pension-legal-page-list-item-4-mybnd')}
        </BNDListArrows>
    );

    const getLegalStatementsMarketing = () => (
        <BNDListArrows>
            {t('pension-legal-page-list-item-1')}
            {t('pension-legal-page-list-item-2-marketing')}
            {t('pension-legal-page-list-item-3-marketing')}
            {t('pension-legal-page-list-item-4-marketing')}
        </BNDListArrows>
    );

    const getLegalText = () => (isReturning ? getLegalTextReturningCustomer() : getLegalTextNewCustomer());

    const getLegalTextReturningCustomer = () => {
        return <span>{t('pension-legal-page-text-returning')}</span>;
    };

    const getLegalTextNewCustomer = () => {
        return (
            <>
                <span>{t('pension-legal-page-text-new-part-1')}</span>
                <span>{t('pension-legal-page-text-new-part-2')}</span>
            </>
        );
    };

    return (
        <div className="flex flex-col gap-6 w-full md:w-3/4 m-auto p-4 sm:p-8">
            <div>
                <span className="font-medium text-3xl">
                    {isReturning ? t('pension-legal-page-title-returning') : t('pension-legal-page-title')}
                </span>
            </div>
            <div className={'flex flex-col gap-6'}>{getLegalText()}</div>
            <div>{getLegalStatements()}</div>
            <div className="flex flex-row gap-4 items-center mt-8">
                <Toggle
                    className="mr-4"
                    icons={false}
                    checked={toggleValue}
                    onChange={(e) => setToggleValue(e.target.checked)}
                />
                <span>{t('pension-legal-page-toggle-text')}</span>
            </div>
            <div>
                <span>
                    {t('pension-legal-page-toggle-disclaimer-prefix-text')}
                    <Link
                        className="text-bnd-green underline"
                        href={PRIVACY_STATEMENT_PENSION}
                        target="_blank"
                        rel="noopener noreferrer"
                        linkColor="green"
                    >
                        {t('pension-legal-page-toggle-disclaimer-link-text')}
                    </Link>
                    {t('pension-legal-page-toggle-disclaimer-suffix-text')}
                </span>
            </div>
            <div>
                <Button
                    className={`w-72 sm:w-80 px-4`}
                    isDisabled={!toggleValue}
                    buttonType={'primary'}
                    showWobbleArrow={true}
                    onClick={() => {
                        history.push('personal');
                    }}
                >
                    {isReturning ? t('pension-legal-page-button-text-returning') : t('pension-legal-page-button-text')}
                </Button>
            </div>
        </div>
    );
};
